
import React, { useState, useEffect } from 'react';
import { Layout, Menu, Dropdown, Avatar } from 'antd';
import { SettingOutlined, NodeIndexOutlined, UserOutlined, LogoutOutlined, CodeOutlined, CheckSquareOutlined, PlayCircleOutlined, DatabaseOutlined } from '@ant-design/icons';
import 'antd/dist/reset.css';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Profile from './components/Profile';
import WorkflowEditor from './components/WorkflowEditor';
import NodeModuleEditor from './components/NodeModuleEditor';
import DynamicModuleEditor from './components/DynamicModuleEditor';
import Settings from './components/Settings';
import JobsView from './components/JobsView';
import TaskOverview from './components/TaskOverview';
import JobMonitor from './components/JobMonitor';
import DatabaseView from './components/DatabaseView';

const { Header, Content } = Layout;

function AppContent() {
  const { user, logout, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get current section from URL path
  const getCurrentSection = () => {
    const path = location.pathname.slice(1); // Remove leading slash
    return path || 'workflow'; // Default to workflow if no path
  };

  const [activeSection, setActiveSection] = useState(getCurrentSection());

  // Update activeSection when URL changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const section = getCurrentSection();
    setActiveSection(section);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  const menuItems = [
    {
      key: 'workflow',
      icon: <NodeIndexOutlined />,
      label: 'Workflows',
      hidden: false,
    },
    {
      key: 'executions',
      icon: <PlayCircleOutlined />,
      label: 'Executions',
      hidden: false,
    },
    {
      key: 'dynamic-modules',
      icon: <CodeOutlined />,
      label: 'Dynamic Modules',
      hidden: true,
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      hidden: false,
    },
    {
      key: 'tasks',
      icon: <CheckSquareOutlined />,
      label: 'Tasks',
      hidden: true,
    },
    {
      key: 'database',
      icon: <DatabaseOutlined />,
      label: 'Tasks',
      hidden: false,
    },
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      hidden: false,
    },
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => {
        navigate('/profile');
        setActiveSection('profile');
      },
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: logout,
    },
  ];

  const handleMenuClick = ({ key }) => {
    navigate(`/${key}`);
    setActiveSection(key);
  };

  return (
    <Layout style={{ height: '100vh' }}>
      <Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ color: '#fff', fontWeight: 'bold', fontSize: 20 }}>
          Spot Desktop
        </div>
        <Menu
          theme="dark"
          mode="horizontal"
          selectedKeys={[activeSection]}
          items={menuItems.filter(item => !item.hidden)}
          onClick={handleMenuClick}
          style={{ flex: 1, justifyContent: 'center', border: 'none' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ color: '#fff' }}>Welcome, {user.username}</span>
          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Avatar
              style={{ backgroundColor: '#1890ff', cursor: 'pointer' }}
              icon={<UserOutlined />}
            />
          </Dropdown>
        </div>
      </Header>
      <Layout style={{ flex: 1, overflow: 'hidden' }}>
        <Content
          style={{
            padding: activeSection === 'workflow' || location.pathname === '/' || location.pathname === '/workflow' ? 0 : 24,
            margin: 0,
            height: '100%',
            background: '#fff',
            overflow: 'hidden'
          }}
        >
          <Routes>
            <Route path="/" element={<WorkflowEditor />} />
            <Route path="/workflow" element={<WorkflowEditor />} />
            <Route path="/executions" element={
              <div style={{ padding: 0 }}>
                <JobMonitor />
              </div>
            } />
            <Route path="/modules" element={<NodeModuleEditor />} />
            <Route path="/dynamic-modules" element={<DynamicModuleEditor />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/jobs" element={<JobsView />} />
            <Route path="/tasks" element={<TaskOverview />} />
            <Route path="/database" element={
              <div style={{ height: '100%', padding: 0 }}>
                <DatabaseView />
              </div>
            } />
            <Route path="/profile" element={<Profile />} />
          </Routes>
        </Content>
      </Layout>
      {/* Only show footer for non-workflow pages 
      {activeSection !== 'workflow' && location.pathname !== '/' && location.pathname !== '/workflow' && (
        <Footer style={{ textAlign: 'center' }}>Spot Desktop ©2025</Footer>
      )} */}
    </Layout>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
